import React from 'react';
import type { Entity } from '../../../shared/types/models/entity';

interface EntityListComponentProps {
  entities: Entity[];
  step: string;
  onOpenModal: (lookup: string, name: string, biometricType?: string) => void;
  modalOpen: null | { lookup: string; name: string };
  onEdit?: (entity: any, idx: number) => void;
  onDelete?: (entity: any, idx: number) => void;
}

/**
 * EntityListComponent renders a list of entity cards with name, address, claimant type, and biometric/device actions.
 * The rendering can be customized based on the current step.
 * Optionally, edit and delete buttons can be shown for each entity if onEdit/onDelete are provided and step is 'grantors'.
 */

// Utility: Determine entity status (success or warning)
export function getEntityStatus(entity: any) {
  // Green check if transactionAccepted is true
  if (entity.transactionAccepted) return 'success';
  // Otherwise, show warning (yellow)
  return 'warning';
}

const EntityListComponent: React.FC<EntityListComponentProps & { showStatus?: boolean }> = ({
  entities,
  step,
  onOpenModal,
  onEdit,
  onDelete,
  showStatus = false,
}) => {
  return (
    <div className="flex flex-col items-center w-full">
      {entities.map((g: any, idx: number) => {
        const m = g.metadata || {};
        const isIndividual = g.type === 'individual' || g.type === 'trustee' || g.type === 'member';
        const name = isIndividual ? (m.fullName || `${m.firstName || ''} ${m.lastName || ''}`.trim()) : m.name || '';
        const address = (
          <span className="font-plus-jakarta text-[14px] text-[#485D7D] whitespace-pre-line leading-snug">
            {m.address}
            {m.address2 ? `\n${m.address2}` : ''}
            {m.city || m.state || m.zip ? `\n${m.city}${m.city && m.state ? ', ' : ''}${m.state}${(m.city || m.state) && m.zip ? ' ' : ''}${m.zip}` : ''}
          </span>
        );
        const status = getEntityStatus(g);
        return (
          <div
            key={g.id ?? idx}
            className="flex flex-row items-center justify-between w-full bg-[#F9FBFD] border border-[#D7DFEC] rounded-lg mb-4 px-4 py-3 gap-4"
            style={{ minHeight: 72 }}
          >
            {/* Left: Name & Address */}
            <div className="flex flex-col min-w-[180px]">
              <span className="font-plus-jakarta font-bold text-[16px] text-[#141435] leading-tight">{name}</span>
              {address}
            </div>
            {/* Middle: Claimant Type Badge */}
            <div className="flex items-center min-w-[124px] justify-center">
              <span
                className={`px-2 py-1 rounded border text-[12px] font-bold font-plus-jakarta ${m.claimantType === 'Participant'
                  ? 'bg-[#EEFFEE] border-[#92C892] text-[#3C7F3C]'
                  : 'bg-[#F5F5F5] border-[#9EAABE] text-[#485D7D]'}`}
                style={{ borderRadius: 4 }}
              >
                {m.claimantType}
              </span>
            </div>
            {/* Right: Status Indicator or Actions */}
            <div className="flex items-center min-w-[180px] justify-end gap-2">
              {showStatus ? (
                status === 'success' ? (
                  <span className="text-[14px] font-medium font-plus-jakarta text-[#3C7F3C] flex items-center gap-2">
                    <svg width="28" height="28" fill="none" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#22c55e"/><path d="M6 10.5l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <span className="sr-only">Photo Taken</span>
                  </span>
                ) : (
                  <span className="text-[14px] font-medium font-plus-jakarta text-[#8A6D1B] flex items-center gap-2">
                    <svg width="28" height="28" fill="none" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#FFD600"/><path d="M10 6v4m0 4h.01" stroke="#8A6D1B" strokeWidth="2" strokeLinecap="round"/></svg>
                    <span className="sr-only">Warning</span>
                  </span>
                )
              ) : step === 'grantors' && (onEdit || onDelete) ? (
                <>
                  {onEdit && (
                    <button
                      type="button"
                      className={`w-8 h-8 flex items-center justify-center bg-[#E5EDFA] border border-[#D7DFEC] rounded hover:bg-[#E5EDFA] transition-colors${g.transactionAccepted ? ' opacity-50 cursor-not-allowed' : ''}`}
                      style={{ borderRadius: 4 }}
                      onClick={() => {
                        console.log('[EntityListComponent] Edit clicked entity:', g);
                        if (!g.transactionAccepted) onEdit(g, idx);
                      }}
                      title="Edit grantor"
                      disabled={!!g.transactionAccepted}
                    >
                      <svg className="w-5 h-5 text-[#9EAABE]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/></svg>
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      className="w-10 h-10 flex items-center justify-center bg-[#F8F8F8] border border-[#D7DFEC] rounded hover:bg-[#F3F4F6] transition-colors"
                      style={{ borderRadius: 4 }}
                      onClick={() => onDelete(g, idx)}
                      title="Remove grantor"
                    >
                      <svg className="w-5 h-5 text-[#9EAABE]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 6h18"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6"/><path d="M19 6l-1.5 14a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  )}
                </>
              ) : m.claimantType === 'Participant' && step === 'biometrics' && g.transactionAccepted && g.type !== 'trust' && g.type !== 'company' ? (
                <span className="text-[14px] font-medium font-plus-jakarta text-[#3C7F3C] flex items-center gap-2">
                  <svg width="28" height="28" fill="none" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#22c55e"/><path d="M6 10.5l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="sr-only">Photo Taken</span>
                </span>
              ) : m.claimantType === 'Participant' && step === 'biometrics' && !g.transactionAccepted && g.type !== 'trust' && g.type !== 'company' ? (
                <button
                  className="bg-[#0B4BB6] text-white font-bold font-plus-jakarta rounded-full px-5 py-2 text-[15px] hover:bg-[#23235a] transition-colors h-[44px] min-w-[120px] flex items-center gap-2"
                  style={{ padding: '10px 20px' }}
                  onClick={() => onOpenModal(g.lookup, name, g.biometricType)}
                >
                  <svg width="20" height="20" viewBox="0 0 60 60" fill="none">
                    <rect x="8" y="16" width="44" height="32" rx="8" fill="#F0F6FF"/>
                    <rect x="8" y="16" width="44" height="32" rx="8" stroke="#0B4BB6" strokeWidth="2"/>
                    <circle cx="30" cy="32" r="10" fill="#0B4BB6"/>
                    <circle cx="30" cy="32" r="6" fill="#fff"/>
                    <rect x="24" y="10" width="12" height="8" rx="4" fill="#0B4BB6"/>
                  </svg>
                  {g.biometricType === 'compare' ? 'Verify Identity' : 'Take Photo'}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EntityListComponent; 